import { useState, useEffect, useCallback } from "react";
import { Building, MapPin, Mail, Send } from "lucide-react";
import { Button } from "../../../../components/ui/button";
import { Input } from "../../../../components/ui/input";
import { useAuth } from "../../../../context/AuthContext";
import Swal from "sweetalert2";
import { generacionLlaveSecretaParaRegistro } from "../../../../api/apartamentoService";
import { obtenerPublicacionActiva, Publicacion } from "../../../../api/tableroInformativoService";
import { ApartamentoConPropietario } from "../../../../api/apartamentoService";

interface EnviarLlaveModalProps {
  isOpen: boolean;
  apartamento: ApartamentoConPropietario | null;
  onClose: () => void;
}

export default function EnviarLlaveModal({ isOpen, apartamento, onClose }: EnviarLlaveModalProps) {
  const { session } = useAuth();
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingInfo, setIsLoadingInfo] = useState(false);
  const [publicacionInfo, setPublicacionInfo] = useState<Publicacion | null>(null);

  const loadApartamentoInfo = useCallback(async () => {
    if (!apartamento || !session?.unidad?.id) return;

    try {
      setIsLoadingInfo(true);
      const response = await obtenerPublicacionActiva({
        codigo: apartamento.apt_codigo,
        unidad_residencial_codigo: session.unidad.id,
        tipo_publicacion_codigo: "VN",
      });

      if (response.code === "1" && response.object_ && response.object_.length > 0) {
        setPublicacionInfo(response.object_[0]);
      }
    } catch (error) {
      console.error("Error al cargar información del apartamento:", error);
    } finally {
      setIsLoadingInfo(false);
    }
  }, [apartamento, session?.unidad?.id]);

  useEffect(() => {
    if (isOpen && apartamento) {
      setEmail("");
      setPublicacionInfo(null);
      loadApartamentoInfo();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, apartamento]);

  const handleSubmit = async () => {
    if (!apartamento) return;

    if (!email.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Campo requerido",
        text: "Por favor ingrese un correo electrónico",
        confirmButtonColor: "#3d5a40",
        didOpen: (popup) => {
          (popup as HTMLElement).style.zIndex = '10001';
        },
      });
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      Swal.fire({
        icon: "warning",
        title: "Correo inválido",
        text: "Por favor ingrese un correo electrónico válido",
        confirmButtonColor: "#3d5a40",
        didOpen: (popup) => {
          (popup as HTMLElement).style.zIndex = '10001';
        },
      });
      return;
    }

    try {
      setIsSubmitting(true);

      const response = await generacionLlaveSecretaParaRegistro({
        apartamento_codigo: apartamento.apt_codigo,
        correo_electronico: email.trim(),
      });

      if (response.code === "1") {
        Swal.fire({
          icon: "success",
          title: "¡Éxito!",
          text: response.message || "La llave de registro ha sido enviada al correo electrónico",
          confirmButtonColor: "#3d5a40",
          didOpen: (popup) => {
            (popup as HTMLElement).style.zIndex = '10001';
          },
        });
        onClose();
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: response.message || "No se pudo enviar la llave de registro",
          confirmButtonColor: "#3d5a40",
          didOpen: (popup) => {
            (popup as HTMLElement).style.zIndex = '10001';
          },
        });
      }
    } catch (error) {
      console.error("Error al enviar llave:", error);
      const errorMessage = error instanceof Error ? error.message : "No se pudo enviar la llave de registro";
      const mensajeTraducido = errorMessage.includes("Request failed") 
        ? "Error al enviar la llave de registro. Por favor, intente nuevamente."
        : errorMessage.includes("Network Error")
        ? "Error de conexión. Verifique su conexión a internet."
        : errorMessage;
      
      Swal.fire({
        icon: "error",
        title: "Error",
        text: mensajeTraducido,
        confirmButtonColor: "#3d5a40",
        didOpen: (popup) => {
          (popup as HTMLElement).style.zIndex = '10001';
        },
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen || !apartamento) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[95vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-4 sm:px-6 py-4 flex items-center justify-between z-10">
          <h3 className="text-lg sm:text-xl font-bold text-military-600 flex items-center gap-2">
            <Mail className="h-5 w-5 sm:h-6 sm:w-6" />
            <span>Enviar Llave de Registro</span>
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition text-2xl font-bold leading-none"
            aria-label="Cerrar"
            disabled={isSubmitting}
          >
            ×
          </button>
        </div>

        <div className="p-4 sm:p-6 space-y-4 sm:space-y-5">
          {isLoadingInfo ? (
            <div className="flex flex-col items-center justify-center py-8">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-t-military-500 border-gray-300 mb-4" />
              <p className="text-gray-600 text-sm">Cargando información...</p>
            </div>
          ) : (
            <>
              <div className="space-y-3">
                <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                  <h4 className="font-semibold text-gray-900 mb-3">Información del Apartamento</h4>
                  
                  <div className="flex items-center gap-2 text-sm text-gray-700">
                    <Building size={16} className="text-gray-500" />
                    <span className="font-medium">Torre:</span>
                    <span>{apartamento.apt_torre_nombre || "N/A"}</span>
                  </div>

                  <div className="flex items-center gap-2 text-sm text-gray-700">
                    <MapPin size={16} className="text-gray-500" />
                    <span className="font-medium">Apartamento:</span>
                    <span>{apartamento.apt_nombre}</span>
                  </div>

                  {apartamento.apt_piso !== undefined && (
                    <div className="flex items-center gap-2 text-sm text-gray-700">
                      <span className="font-medium">Piso:</span>
                      <span>{apartamento.apt_piso}</span>
                    </div>
                  )}

                  {publicacionInfo && (
                    <>
                      {publicacionInfo.tin_titulo && (
                        <div className="mt-3 pt-3 border-t border-gray-200">
                          <p className="text-xs text-gray-500 mb-1">Título:</p>
                          <p className="text-sm font-medium text-gray-900">{publicacionInfo.tin_titulo}</p>
                        </div>
                      )}
                      {publicacionInfo.tin_descripcion && (
                        <div className="mt-2">
                          <p className="text-xs text-gray-500 mb-1">Descripción:</p>
                          <p className="text-sm text-gray-700">{publicacionInfo.tin_descripcion}</p>
                        </div>
                      )}
                    </>
                  )}
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    <Mail size={16} className="inline mr-1" />
                    Correo Electrónico del Propietario
                  </label>
                  <Input
                    type="email"
                    placeholder="ejemplo@correo.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full"
                    disabled={isSubmitting}
                  />
                  <p className="text-xs text-gray-500">
                    Se enviará una llave de registro a este correo para que el propietario pueda registrarse desde la app
                  </p>
                </div>
              </div>

              <div className="flex gap-3 pt-4 border-t border-gray-200">
                <Button
                  onClick={handleSubmit}
                  disabled={isSubmitting || !email.trim()}
                  className="flex-1 bg-military-500 hover:bg-military-600 text-white"
                >
                  {isSubmitting ? (
                    <>
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent mr-2" />
                      Enviando...
                    </>
                  ) : (
                    <>
                      <Send size={16} className="mr-2" />
                      Enviar
                    </>
                  )}
                </Button>
                <Button
                  onClick={onClose}
                  variant="outline"
                  disabled={isSubmitting}
                  className="flex-1"
                >
                  Cancelar
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

